<script lang="ts">
  import { Refund, newRefund, validateRefund } from "@models/refunds/Refund"
  import {
    RefundAvailability,
    findRefundAvailability,
    maxRefundable,
    convenienceFees,
  } from "@models/refunds/RefundAvailability"
  import { post } from "@util/api"

  import AmountInput from "@components/ui/AmountInput.svelte"
  import BooleanCheckbox from "@components/ui/BooleanCheckbox.svelte"
  import Label from "@components/ui/Label.svelte"
  import RadioButton from "@components/ui/RadioButton.svelte"
  import RadioButtons from "@components/ui/RadioButtons.svelte"
  import Form from "@components/ui/Form.svelte"
  import ErrorMessage from "@components/ui/ErrorMessage.svelte"
  import DangerousAction from "@components/ui/DangerousAction.svelte"
  import InlineTip from "@components/ui/InlineTip.svelte"
  import RefundMathBreakdown from "./RefundForm/RefundMathBreakdown.svelte"
  import SubmitButton from "@components/ui/SubmitButton.svelte"
  import { ValidationErrors } from "@util/validations"
  import Fieldset from "@components/ui/Fieldset.svelte"
  import { ParticipationOption } from "@models/attendance/ParticipationOption"

  interface ChargeOption {
    id: number
    description: string
  }

  export let path: string
  export let refund: Refund
  export let participationOptions: ParticipationOption[]
  export let chargeOptions: ChargeOption[]
  export let refundAvailabilities: RefundAvailability[]

  refund = newRefund(refund)

  $: refundAvailability = findRefundAvailability(refundAvailabilities, refund.chargeId, refund.participationId)
  $: manualRefundOnly = refundAvailability?.refundableViaStripe === false
  $: fees = refundAvailability && convenienceFees(refundAvailability)

  function submit() {
    return post(path, { refund })
  }

  function setToManualIfStripeNotSupported() {
    if (manualRefundOnly) {
      refund.method = "manual"
    }
  }

  function validate(): ValidationErrors<Refund> {
    return validateRefund(refund, refundAvailability && maxRefundable(refundAvailability, refund.includeFees))
  }
</script>

<Form data={refund} {validate} {submit} on:change={setToManualIfStripeNotSupported} let:submitting let:submitted let:errors>
  <Fieldset legend="What to Refund">
    <div slot="description">
      <p>Select a passenger and charge to refund.</p>
      <p>Fully refunding a given charge or passenger may require submitting this form multiple times.</p>
    </div>
    <div>
      <Label name="participationId">Passenger</Label>
      <select id="participationId" name="participationId" bind:value={refund.participationId}>
        <option value={undefined} disabled>Choose a Passenger</option>
        {#each participationOptions as option}
          <option value={option.id}>{option.passengerName}</option>
        {/each}
      </select>
      <ErrorMessage when={submitted} error={errors.participationId} />
    </div>

    <div>
      <Label name="chargeId">Charge to Refund</Label>
      <select id="chargeId" name="chargeId" bind:value={refund.chargeId}>
        <option value={undefined} disabled>Choose a Charge to Refund</option>
        {#each chargeOptions as option}
          <option value={option.id}>{option.description}</option>
        {/each}
      </select>
      <ErrorMessage when={submitted} error={errors.chargeId} />
    </div>

    <div>
      {#if refundAvailability}
        <RefundMathBreakdown {refundAvailability} {refund} {fees} />
      {:else}
        <InlineTip>Select a charge and passenger to see details about what you can refund.</InlineTip>
      {/if}
    </div>
  </Fieldset>

  <Fieldset legend="Refund Options">
    <div slot="description">
      <p>Electronic refunds will refund the money to the card or bank account used to make the payment.</p>
      <p>
        If you wish to transfer money to someone else choose a manual refund here, then create a manual charge for the
        intended recipient.
      </p>

      <p>Please note that:</p>

      <ul class="mt-1 italic text-sm2">
        <li>Card refunds are processed on the same business day.</li>
        <li>Bank refunds can take 2-10 business days to process.</li>
      </ul>
    </div>
    <div>
      <Label name="method">Refund Method</Label>
      <RadioButtons>
        <RadioButton name="method" bind:group={refund.method} value="stripe" disabled={manualRefundOnly}
          >Electronic Refund</RadioButton
        >
        <RadioButton name="method" bind:group={refund.method} value="manual">Manual Refund</RadioButton>
      </RadioButtons>
      <ErrorMessage when={submitted} error={errors.method} />
    </div>

    {#if refundAvailability}
      {#if manualRefundOnly}
        <InlineTip>
          {#if refundAvailability.paymentType === "bank_account"}
            Our payment gateway does not allow refunds on bank payments older than 180 days.
          {:else}
            Only electronic payments can be refunded electronically.
          {/if}
        </InlineTip>
      {/if}

      {#if fees > 0}
        <BooleanCheckbox name="includeFees" bind:value={refund.includeFees} label="I want to refund convenience fees" />
      {/if}

      {#if refund.includeFees}
        <div class="mt-1 mb-4">
          <DangerousAction>
            <h2 class="text-gray-900 text-lg mb-2">Avoid Refunding Convenience Fees</h2>
            <p class="max-w-para whitespace-normal">
              Refunding fees means you will refund more money than you received from this person. We strongly discourage
              refunding fees.
            </p>
          </DangerousAction>
        </div>
      {/if}
    {/if}

    <div>
      <Label name="grossAmount">Amount</Label>
      <AmountInput name="grossAmount" bind:value={refund.grossAmount} />
      <ErrorMessage when={submitted} error={errors.grossAmount} />
    </div>

    {#if refund.method === "manual"}
      <div>
        <Label name="reference">Reference</Label>
        <input
          name="reference"
          id="reference"
          type="text"
          bind:value={refund.reference}
          placeholder="A check #, reference # etc."
        />
        <ErrorMessage when={submitted} error={errors.reference} />
      </div>
    {/if}

    <div>
      <Label name="reason">Reason</Label>
      <input id="reason" name="reason" type="text" bind:value={refund.reason} />
      <ErrorMessage when={submitted} error={errors.reason} />
    </div>

    <div class="button-row">
      <SubmitButton {submitting}>Issue Refund</SubmitButton>
    </div>
  </Fieldset>
</Form>
